import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Assignment,
    PendingActions,
    CheckCircle,
    Add as AddIcon,
    ShoppingCart,
    Description,
    Handshake,
    SyncAlt,
    // AccountBalance,
    // Cancel,
    // Undo,
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../store';
import { fetchMyPRs, fetchPendingApprovals } from '../store/slices/prSlice';
import { fetchBudgetUtilization } from '../store/slices/budgetSlice';
import { useNavigate } from 'react-router-dom';
import GoldenArrow from '../components/common/GoldenArrow';
import apiService from '../services/api.service';

const toDDMMYYYY = (v: any): string => {
    const s = String(v || '').trim();
    if (!s) return '';
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
    const compact = s.match(/^(\d{8})$/);
    if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    }
    return s;
};

type DashboardStat = {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    path?: string;
};

const Dashboard: React.FC<{ prOnly?: boolean }> = ({ prOnly = false }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);
    const { myPRs, pendingApprovals, isLoading } = useSelector((state: RootState) => state.pr);
    const { budgetUtilization } = useSelector((state: RootState) => state.budget);
    const theme = useTheme();
    const navigate = useNavigate();
    const [detailOpen, setDetailOpen] = useState(false);
    const [detail] = useState<any | null>(null);
    

    const [myDrafts, setMyDrafts] = useState<any[]>([]);
    const [myPOs, setMyPOs] = useState<any[]>([]);
    const [myPODrafts, setMyPODrafts] = useState<any[]>([]);
    const [pendingPOApprovals, setPendingPOApprovals] = useState<any[]>([]);
    const [subcontractStatsData, setSubcontractStatsData] = useState<{ total: number; active: number; draft: number; posted: number; paymentCertDrafts: number; workDoneEntries: number }>({
        total: 0,
        active: 0,
        draft: 0,
        posted: 0,
        paymentCertDrafts: 0,
        workDoneEntries: 0,
    });
    const [paymentRequestStats, setPaymentRequestStats] = useState<{
        total: number;
        myDrafts: number;
        pendingMyAction: number;
        inProgress: number;
        finalApproved: number;
        executed: number;
        rejectedReturned: number;
    }>({
        total: 0,
        myDrafts: 0,
        pendingMyAction: 0,
        inProgress: 0,
        finalApproved: 0,
        executed: 0,
        rejectedReturned: 0,
    });
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    // const [approvalActivity, setApprovalActivity] = useState<any>({ approved: 0, rejected: 0, returned: 0 });
    const allowedModules: string[] = Array.isArray((user as any)?.allowedmodules) ? ((user as any)?.allowedmodules as string[]) : [];
    const hasModule = (moduleKey: string) => allowedModules.includes(moduleKey);
    const resolvePath = (...candidates: Array<{ moduleKey: string; path: string }>) =>
        candidates.find((candidate) => hasModule(candidate.moduleKey))?.path;
    const canViewPRKpis = prOnly || allowedModules.includes('dashboard-pr-kpis');
    const canViewPOKpis = !prOnly && allowedModules.includes('dashboard-po-kpis');
    const canViewSubcontractKpis = !prOnly && allowedModules.includes('dashboard-subcontract-kpis');
    const canViewPaymentRequestKpis = !prOnly && allowedModules.includes('dashboard-payment-request-kpis');
    const hasAnyDashboardContent = canViewPRKpis || canViewPOKpis || canViewSubcontractKpis || canViewPaymentRequestKpis;

    const loadSupplementaryDashboardData = async () => {
        if (!canViewPRKpis) {
            setMyDrafts([]);
        }
        if (canViewPOKpis) {
            try {
                const [poRes, poDraftRes, poPendingRes] = await Promise.all([
                    apiService.getMyPOs(),
                    apiService.getMyPODrafts(),
                    apiService.getPendingApprovalsPO(),
                ]);
                setMyPOs(Array.isArray((poRes as any)?.data) ? (poRes as any).data : []);
                setMyPODrafts(Array.isArray((poDraftRes as any)?.data) ? (poDraftRes as any).data : []);
                setPendingPOApprovals(Array.isArray((poPendingRes as any)?.data) ? (poPendingRes as any).data : []);
            } catch {
                setMyPOs([]);
                setMyPODrafts([]);
                setPendingPOApprovals([]);
            }
        } else {
            setMyPOs([]);
            setMyPODrafts([]);
            setPendingPOApprovals([]);
        }

        if (canViewSubcontractKpis) {
            try {
                const subcontractStatsRes = await apiService.getSubcontractStats();
                const stats = (subcontractStatsRes as any)?.data || subcontractStatsRes || {};
                setSubcontractStatsData({
                    total: Number(stats.total || 0),
                    active: Number(stats.active || 0),
                    draft: Number(stats.draft || 0),
                    posted: Number(stats.posted || 0),
                    paymentCertDrafts: Number(stats.paymentcertdrafts || 0),
                    workDoneEntries: Number(stats.workdoneentries || 0),
                });
            } catch {
                setSubcontractStatsData({ total: 0, active: 0, draft: 0, posted: 0, paymentCertDrafts: 0, workDoneEntries: 0 });
            }
        } else {
            setSubcontractStatsData({ total: 0, active: 0, draft: 0, posted: 0, paymentCertDrafts: 0, workDoneEntries: 0 });
        }

        if (canViewPaymentRequestKpis) {
            try {
                const prReqStatsRes = await apiService.getPaymentRequestStats();
                const stats = (prReqStatsRes as any)?.data || {};
                setPaymentRequestStats({
                    total: Number(stats.total || 0),
                    myDrafts: Number(stats.myDrafts || 0),
                    pendingMyAction: Number(stats.pendingMyAction || 0),
                    inProgress: Number(stats.inProgress || 0),
                    finalApproved: Number(stats.finalApproved || 0),
                    executed: Number(stats.executed || 0),
                    rejectedReturned: Number(stats.rejectedReturned || 0),
                });
            } catch {
                setPaymentRequestStats({
                    total: 0, myDrafts: 0, pendingMyAction: 0,
                    inProgress: 0, finalApproved: 0, executed: 0, rejectedReturned: 0,
                });
            }
        } else {
            setPaymentRequestStats({
                total: 0, myDrafts: 0, pendingMyAction: 0,
                inProgress: 0, finalApproved: 0, executed: 0, rejectedReturned: 0,
            });
        }
    };

    // Fetch data on mount and when company changes
    useEffect(() => {
        const currentCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || null;
        setSelectedCompanyId(currentCompanyId);

        if (canViewPRKpis) {
            dispatch(fetchMyPRs());
            dispatch(fetchPendingApprovals());
            dispatch(fetchBudgetUtilization({}));
            (async () => {
                try { const r = await apiService.getMyDrafts(); const arr = (r as any)?.data || r || []; setMyDrafts(Array.isArray(arr) ? arr : []); } catch { setMyDrafts([]); }
            })();
        } else {
            setMyDrafts([]);
        }
        void loadSupplementaryDashboardData();
        // (async () => {
        //     try { const r = await apiService.getApprovalActivityStats(); const stats = (r as any)?.data || r || { approved: 0, rejected: 0, returned: 0 }; setApprovalActivity(stats); } catch { setApprovalActivity({ approved: 0, rejected: 0, returned: 0 }); }
        // })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, canViewPOKpis, canViewSubcontractKpis]);

    // Listen for company changes
    useEffect(() => {
        const handleCompanyChange = () => {
            const newCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || null;
            if (newCompanyId !== selectedCompanyId) {
                setSelectedCompanyId(newCompanyId);
                if (canViewPRKpis) {
                    dispatch(fetchMyPRs());
                    dispatch(fetchPendingApprovals());
                    dispatch(fetchBudgetUtilization({}));
                    (async () => {
                        try { const r = await apiService.getMyDrafts(); const arr = (r as any)?.data || r || []; setMyDrafts(Array.isArray(arr) ? arr : []); } catch { setMyDrafts([]); }
                    })();
                } else {
                    setMyDrafts([]);
                }
                void loadSupplementaryDashboardData();
                // (async () => {
                //     try { const r = await apiService.getApprovalActivityStats(); const stats = (r as any)?.data || r || { approved: 0, rejected: 0, returned: 0 }; setApprovalActivity(stats); } catch { setApprovalActivity({ approved: 0, rejected: 0, returned: 0 }); }
                // })();
            }
        };

        // Poll for company changes
        const interval = setInterval(handleCompanyChange, 1000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId, dispatch, canViewPOKpis, canViewSubcontractKpis]);

    const prStats: DashboardStat[] = [
        {
            title: 'My PRs',
            value: myPRs.length,
            icon: <Assignment sx={{ fontSize: 40 }} />,
            color: theme.palette.primary.main,
            path: resolvePath({ moduleKey: 'in-progress', path: '/in-progress' }),
        },
        {
            title: 'My Drafts',
            value: myDrafts.length,
            icon: <Assignment sx={{ fontSize: 40 }} />,
            color: theme.palette.info.main,
            path: resolvePath({ moduleKey: 'create-pr', path: '/create-pr' }),
        },
        {
            title: 'Pending Approvals',
            value: pendingApprovals.length,
            icon: <PendingActions sx={{ fontSize: 40 }} />,
            color: theme.palette.warning.main,
            path: resolvePath({ moduleKey: 'inbox', path: '/inbox' }),
        },
        {
            title: 'My PRs Pending',
            value: myPRs.filter((pr: any) => pr.status === 'Pending').length,
            icon: <PendingActions sx={{ fontSize: 40 }} />,
            color: '#ff9800',
            path: resolvePath({ moduleKey: 'in-progress', path: '/in-progress' }),
        },
        {
            title: 'My PRs Approved',
            value: myPRs.filter((pr: any) => pr.status === 'Approved').length,
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            color: theme.palette.success.main,
            path: resolvePath({ moduleKey: 'completed', path: '/completed' }),
        },
        // Temporarily hidden stat cards
        // {
        //     title: 'My PRs Rejected',
        //     value: myPRs.filter((pr: any) => pr.status === 'Rejected').length,
        //     icon: <Cancel sx={{ fontSize: 40 }} />,
        //     color: theme.palette.error.main,
        // },
        // {
        //     title: 'My PRs Returned',
        //     value: myPRs.filter((pr: any) => {
        //         const lastAction = String(pr.lastaction || '').toLowerCase();
        //         return lastAction === 'returned' && pr.status !== 'Rejected' && pr.status !== 'Approved';
        //     }).length,
        //     icon: <Undo sx={{ fontSize: 40 }} />,
        //     color: '#9c27b0',
        // },
        // {
        //     title: "I've Approved",
        //     value: approvalActivity.approved,
        //     icon: <CheckCircle sx={{ fontSize: 40 }} />,
        //     color: '#4caf50',
        // },
        // {
        //     title: "I've Rejected",
        //     value: approvalActivity.rejected,
        //     icon: <Cancel sx={{ fontSize: 40 }} />,
        //     color: '#f44336',
        // },
        // {
        //     title: "I've Returned",
        //     value: approvalActivity.returned,
        //     icon: <Undo sx={{ fontSize: 40 }} />,
        //     color: '#9c27b0',
        // },
        // {
        //     title: 'Budget Items',
        //     value: Array.isArray(budgetUtilization) ? budgetUtilization.length : 0,
        //     icon: <AccountBalance sx={{ fontSize: 40 }} />,
        //     color: theme.palette.secondary.main,
        // },
    ];

    const poStats: DashboardStat[] = [
        {
            title: 'My POs',
            value: myPOs.length,
            icon: <ShoppingCart sx={{ fontSize: 40 }} />,
            color: '#1976d2',
            path: resolvePath({ moduleKey: 'create-po', path: '/po-tracker?view=all' }),
        },
        {
            title: 'My PO Drafts',
            value: myPODrafts.length,
            icon: <Description sx={{ fontSize: 40 }} />,
            color: '#fbc02d',
            path: resolvePath({ moduleKey: 'create-po', path: '/po-tracker?view=drafts' }),
        },
        {
            title: 'PO Pending Approvals',
            value: pendingPOApprovals.length,
            icon: <PendingActions sx={{ fontSize: 40 }} />,
            color: '#ef6c00',
            path: resolvePath(
                { moduleKey: 'create-po', path: '/po-tracker?view=pending-approvals' },
                { moduleKey: 'inbox', path: '/inbox' }
            ),
        },
        {
            title: 'My POs Approved',
            value: myPOs.filter((po: any) => ['approved', 'postedtosap'].includes(String(po.status || '').toLowerCase())).length,
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            color: theme.palette.success.main,
            path: resolvePath({ moduleKey: 'create-po', path: '/po-tracker?view=approved' }),
        },
    ];

    const subcontractStats: DashboardStat[] = [
        {
            title: 'My Subcontracts',
            value: subcontractStatsData.total,
            icon: <Handshake sx={{ fontSize: 40 }} />,
            color: '#7b1fa2',
            path: resolvePath(
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' },
                { moduleKey: 'create-subcontract', path: '/subcontract/create' }
            ),
        },
        {
            title: 'Active Subcontracts',
            value: subcontractStatsData.active,
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            color: '#388e3c',
            path: resolvePath(
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' },
                { moduleKey: 'create-subcontract', path: '/subcontract/create' }
            ),
        },
        {
            title: 'Draft Subcontracts',
            value: subcontractStatsData.draft,
            icon: <Description sx={{ fontSize: 40 }} />,
            color: '#f57c00',
            path: resolvePath(
                { moduleKey: 'create-subcontract', path: '/subcontract/create' },
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' }
            ),
        },
        {
            title: 'SAP Posted',
            value: subcontractStatsData.posted,
            icon: <SyncAlt sx={{ fontSize: 40 }} />,
            color: '#0288d1',
            path: resolvePath(
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' },
                { moduleKey: 'create-subcontract', path: '/subcontract/create' }
            ),
        },
        {
            title: 'PC Drafts',
            value: subcontractStatsData.paymentCertDrafts,
            icon: <Description sx={{ fontSize: 40 }} />,
            color: '#6d4c41',
            path: resolvePath(
                { moduleKey: 'payment-certificate', path: '/subcontract/payment-certificate' },
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' }
            ),
        },
        {
            title: 'Work Done Entries',
            value: subcontractStatsData.workDoneEntries,
            icon: <Assignment sx={{ fontSize: 40 }} />,
            color: '#5c6bc0',
            path: resolvePath(
                { moduleKey: 'subcontract-work-done', path: '/subcontract/work-done' },
                { moduleKey: 'subcontract-documents', path: '/subcontract/documents' }
            ),
        },
    ];

    const paymentRequestStatCards: DashboardStat[] = [
        {
            title: 'Total Payment Requests',
            value: paymentRequestStats.total,
            icon: <Assignment sx={{ fontSize: 40 }} />,
            color: '#1976d2',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests' }),
        },
        {
            title: 'My Drafts',
            value: paymentRequestStats.myDrafts,
            icon: <Description sx={{ fontSize: 40 }} />,
            color: '#fbc02d',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests?status=Draft' }),
        },
        {
            title: 'Pending My Action',
            value: paymentRequestStats.pendingMyAction,
            icon: <PendingActions sx={{ fontSize: 40 }} />,
            color: '#ef6c00',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests?pendingMyAction=1' }),
        },
        {
            title: 'In Progress',
            value: paymentRequestStats.inProgress,
            icon: <SyncAlt sx={{ fontSize: 40 }} />,
            color: '#0288d1',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests' }),
        },
        {
            title: 'Final Approved',
            value: paymentRequestStats.finalApproved,
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            color: '#2e7d32',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests?status=FinalApproved' }),
        },
        {
            title: 'Executed',
            value: paymentRequestStats.executed,
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            color: '#388e3c',
            path: resolvePath({ moduleKey: 'create-payment-request', path: '/payment-requests?status=Executed' }),
        },
    ];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    const renderStatSection = (title: string, caption: string, stats: DashboardStat[]) => (
        <Paper
            sx={{
                p: 2.5,
                mt: 3,
                borderRadius: 2.5,
                border: '1px solid #e7ebf0',
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '1px solid #eef2f6',
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#243447' }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {caption}
                    </Typography>
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 999,
                        backgroundColor: '#eef4fb',
                        color: '#46627f',
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                    }}
                >
                    {stats.length} cards
                </Typography>
            </Box>

            <Grid container spacing={2.5}>
                {stats.map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.title}>
                        <Card
                            onClick={stat.path ? () => navigate(stat.path as string) : undefined}
                            sx={{
                                height: '100%',
                                minHeight: 148,
                                borderRadius: 2,
                                border: '1px solid #e8edf3',
                                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
                                background: 'linear-gradient(180deg, #ffffff 0%, #fdfefe 100%)',
                                cursor: stat.path ? 'pointer' : 'default',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                                '&:hover': stat.path ? {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
                                    borderColor: alpha(stat.color, 0.35),
                                } : undefined,
                            }}
                        >
                            <CardContent sx={{ height: '100%', p: 2.25 }}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1.5,
                                            backgroundColor: alpha(stat.color, 0.12),
                                            color: stat.color,
                                        }}
                                    >
                                        {stat.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                color: '#6b7a8c',
                                                fontWeight: 700,
                                                letterSpacing: 0.45,
                                                display: 'block',
                                                mb: 0.5,
                                            }}
                                        >
                                            {stat.title}
                                        </Typography>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                lineHeight: 1,
                                                fontWeight: 700,
                                                color: '#1f2d3d',
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );

    const dashboardActionButtonSx = {
        minWidth: 140,
        justifyContent: 'center',
        borderRadius: 1.5,
        fontWeight: 700,
    } as const;

    return (
        <Box
            component="section"
            aria-labelledby="dashboard-heading"
            sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}
        >
            <Paper
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: '1px solid #e6ebf1',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f6faff 100%)',
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography
                            component="h1"
                            id="dashboard-heading"
                            variant="h4"
                            gutterBottom
                            sx={{
                                mb: 0.5,
                                color: '#223043',
                                fontWeight: 700,
                                fontSize: { xs: 22, sm: 26, md: 30 },
                                wordBreak: 'break-word',
                            }}
                        >
                            {prOnly ? 'PR Dashboard' : `Welcome, ${user?.fullname}`}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            {user?.department} - {user?.role}
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', md: 'auto' } }}>
                        {allowedModules.includes('create-pr') && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/create-pr')}
                                sx={dashboardActionButtonSx}
                            >
                                New PR
                            </Button>
                        )}
                        {!prOnly && allowedModules.includes('create-po') && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/create-po')}
                                sx={dashboardActionButtonSx}
                            >
                                New PO
                            </Button>
                        )}
                        {!prOnly && allowedModules.includes('create-subcontract') && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/subcontract/create')}
                                sx={dashboardActionButtonSx}
                            >
                                New Subcontract
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            {canViewPRKpis && renderStatSection('PR KPIs', 'Purchase requisition snapshot for the selected company.', prStats)}

            {canViewPOKpis && renderStatSection('PO KPIs', 'Purchase order activity, drafts, and approval workload.', poStats)}

            {canViewSubcontractKpis && renderStatSection('Subcontract KPIs', 'Subcontract progress, SAP posting, payment certificate drafts, and work-done activity.', subcontractStats)}

            {canViewPaymentRequestKpis && renderStatSection('Payment Request KPIs', 'Payment Request pipeline — drafts, approvals waiting on you, and execution status.', paymentRequestStatCards)}

            {canViewPRKpis && (
                <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 1.5, color: '#243447', fontWeight: 700 }}>
                        Operational Snapshot
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ border: '1px solid #eaecee', borderRadius: 2, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Recent PRs
                                    </Typography>
                                    {myPRs.slice(0, 5).map((pr: any) => (
                                        <Box key={pr.prid} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body1" sx={{ flexGrow: 1 }}>{pr.prnumber}</Typography>
                                                <GoldenArrow prId={Number(pr.prid)} />
                                            </Stack>
                                            <Typography variant="body2" color="textSecondary">
                                                {pr.allocationtype}: {pr.allocationcode} - {pr.status}
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ border: '1px solid #eaecee', borderRadius: 2, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Pending Approvals
                                    </Typography>
                                    {pendingApprovals.slice(0, 5).map((pr: any) => (
                                        <Box key={pr.prid} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body1" sx={{ flexGrow: 1 }}>{pr.prnumber}</Typography>
                                                <GoldenArrow prId={Number(pr.prid)} />
                                            </Stack>
                                            <Typography variant="body2" color="textSecondary">
                                                Requested by: {pr.requestedbyname} - {pr.dayspending} days pending
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ border: '1px solid #eaecee', borderRadius: 2, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        My Drafts
                                    </Typography>
                                    {myDrafts.slice(0, 5).map((pr: any) => (
                                        <Box key={pr.prid} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body1" sx={{ flexGrow: 1 }}>{pr.prnumber}</Typography>
                                                <GoldenArrow prId={Number(pr.prid)} />
                                            </Stack>
                                            <Typography variant="body2" color="textSecondary">
                                                Needed By: {toDDMMYYYY(pr.requireddate)} - {pr.allocationtype} {pr.allocationcode}
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ border: '1px solid #eaecee', borderRadius: 2, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Budget Utilization
                                    </Typography>
                                    {(Array.isArray(budgetUtilization) ? budgetUtilization.slice(0, 5) : []).map((b: any, idx: number) => (
                                        <Box key={idx} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                                    {(b.allocationtype || '')} {(b.allocationcode || '')} / {(b.accountcode || '')}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body2" color="textSecondary">
                                                Utilization: {Number(b.utilizationpercentage || 0).toFixed(2)}% • Pending: {Number(b.pendingamount || 0).toFixed(2)} • Available: {Number(b.availableamount || 0).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {(!Array.isArray(budgetUtilization) || budgetUtilization.length === 0) && (
                                        <Typography variant="body2" color="textSecondary">No budget data</Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {!hasAnyDashboardContent && (
                <Paper
                    sx={{
                        mt: 3,
                        p: 3,
                        borderRadius: 2.5,
                        border: '1px solid #e7ebf0',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#243447' }}>
                        No Dashboard Widgets Assigned
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Assign dashboard KPI modules or operational modules in User Management to show content here.
                    </Typography>
                </Paper>
            )}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>PR Details</DialogTitle>
                <DialogContent dividers>
                    {detail ? (
                        <Stack spacing={1}>
                            <Typography variant="subtitle2">PR No: {detail.prnumber}</Typography>
                            <Typography variant="body2">Type: {detail.requisitiontype}</Typography>
                            <Typography variant="body2">Allocation: {detail.allocationtype} / {detail.allocationcode}</Typography>
                            <Typography variant="body2">Needed By: {toDDMMYYYY(detail.requireddate)}</Typography>
                            <Typography variant="body2">Status: {detail.status}</Typography>
                            <Typography variant="body2">Total: {Number(detail.totalamount || 0).toFixed(2)} {detail.currency}</Typography>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dashboard;
